"use client";

import type { ReportMeasure, ReportParts } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React from "react";
import { formatParts, formatValue } from "../lib/formatValue";

// The validated chart pair: current in blue, previous in neutral. A second
// measure drawn beside the first is a dark orange: apart from the blue for
// every kind of colour vision, and not the amber the annotations use.
export const CURRENT = "#2a78d6";
export const CURRENT_SOFT = "#86b6ef";
export const PREVIOUS = "#a3a3a3";
export const PAIRED = "#b8651a";

export const bucketLabel = (bucket: string, grain: string): string => {
  const date = new Date(`${bucket}T00:00:00`);
  if (grain === "month") {
    return date.toLocaleDateString("en-US", { month: "short" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** The bucket in full, for a tooltip: "Tue, Sep 1", "Week of Sep 1", "September 2026". */
export const bucketTitle = (bucket: string, grain: string): string => {
  const date = new Date(`${bucket}T00:00:00`);
  if (grain === "month") {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  const day = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
  return grain === "week" ? `Week of ${day}` : day;
};

interface TooltipBoxProps {
  title?: string;
  children: React.ReactNode;
}

/** The dark box every chart tooltip sits in. */
export const TooltipBox: React.FC<TooltipBoxProps> = ({ title, children }) => (
  <div className="flex min-w-[170px] flex-col gap-1 rounded-lg bg-neutral-900 px-3 py-2 text-white text-xs shadow-xl">
    {title ? <span className="font-medium">{title}</span> : null}
    {children}
  </div>
);

interface TooltipRowProps {
  color?: string;
  label: string;
  value: string;
  isMuted?: boolean;
}

export const TooltipRow: React.FC<TooltipRowProps> = ({
  color,
  label,
  value,
  isMuted
}) => (
  <span
    className={cn(
      "flex items-center justify-between gap-3",
      isMuted ? "text-neutral-400" : "text-neutral-200"
    )}
  >
    <span className="flex min-w-0 items-center gap-1.5">
      {color ? (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
      ) : null}
      <span className="truncate">{label}</span>
    </span>
    <span
      className={cn("tabular-nums", !isMuted && "font-semibold text-white")}
    >
      {value}
    </span>
  </span>
);

export type TrendPoint = {
  bucket: string;
  value: number;
  previous?: number;
  parts: ReportParts | null;
  /** The second measure's value at the bucket, on a paired chart. */
  paired?: number;
};

interface TrendTooltipProps {
  /** Filled in by the chart. */
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: TrendPoint }>;
  measure: ReportMeasure;
  unit?: string;
  grain: string;
  /** What happened on a bucket's day, by bucket. */
  notes?: Record<string, string[]>;
  /** How the value reads when it is not the actual one ("Running total"). */
  modeNote?: string;
  /** On a paired chart, what each line is called. */
  labels?: { own: string; paired: string };
}

/**
 * A bucket's current and previous values, the numbers behind a ratio, and
 * what happened that day. On a paired chart, both measures by name.
 */
export const TrendTooltip: React.FC<TrendTooltipProps> = ({
  active,
  payload,
  measure,
  unit,
  grain,
  notes,
  modeNote,
  labels
}) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) {
    return null;
  }
  const parts = formatParts(measure, point.parts, unit);
  const happened = notes?.[point.bucket] ?? [];
  const title = bucketTitle(point.bucket, grain);
  return (
    <TooltipBox title={labels && modeNote ? `${title} · ${modeNote}` : title}>
      <TooltipRow
        color={CURRENT}
        label={labels ? labels.own : (modeNote ?? "Current")}
        value={formatValue(measure, point.value)}
      />
      {parts ? (
        <span className="text-[11px] text-neutral-400">{parts}</span>
      ) : null}
      {labels && point.paired !== undefined ? (
        <TooltipRow
          color={PAIRED}
          label={labels.paired}
          value={formatValue(measure, point.paired)}
        />
      ) : null}
      {point.previous !== undefined ? (
        <TooltipRow
          color={PREVIOUS}
          label="Previous"
          value={formatValue(measure, point.previous)}
          isMuted
        />
      ) : null}
      {happened.map((label) => (
        <span key={label} className="text-[#f0c060] text-[11px]">
          {label}
        </span>
      ))}
    </TooltipBox>
  );
};

export type LegendEntry = {
  key: string;
  label: string;
  color: string;
  isHidden?: boolean;
};

interface SeriesLegendProps {
  entries: LegendEntry[];
  /** Click an entry to hide or show it. */
  onToggle?: (key: string) => void;
  /** Hover an entry to light it up in the chart; null when the mouse leaves. */
  onHover?: (key: string | null) => void;
  className?: string;
}

export const SeriesLegend: React.FC<SeriesLegendProps> = ({
  entries,
  onToggle,
  onHover,
  className
}) => (
  <div
    className={cn(
      "flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]",
      className
    )}
  >
    {entries.map((entry) => (
      <button
        type="button"
        key={entry.key}
        onClick={onToggle ? () => onToggle(entry.key) : undefined}
        onMouseEnter={
          onHover ? () => onHover(entry.isHidden ? null : entry.key) : undefined
        }
        onMouseLeave={onHover ? () => onHover(null) : undefined}
        disabled={!onToggle}
        title={onToggle ? (entry.isHidden ? "Show" : "Hide") : undefined}
        className={cn(
          "flex items-center gap-1.5 text-neutral-600",
          onToggle && "hover:text-neutral-900",
          entry.isHidden && "line-through opacity-50"
        )}
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
          style={{ backgroundColor: entry.color }}
        />
        <span className="max-w-[180px] truncate">{entry.label}</span>
      </button>
    ))}
  </div>
);

interface SegmentedProps<T extends string> {
  options: { key: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}

/** A small two-or-three-way switch, like the form picker. */
export const Segmented = <T extends string>({
  options,
  value,
  onChange
}: SegmentedProps<T>): React.ReactElement => (
  <div className="flex items-center gap-0.5 rounded-md border border-neutral-200 p-0.5">
    {options.map((option) => (
      <button
        type="button"
        key={option.key}
        onClick={() => onChange(option.key)}
        className={cn(
          "h-[22px] rounded px-2 text-[11.5px]",
          value === option.key
            ? "bg-neutral-900 font-medium text-white"
            : "text-neutral-500 hover:bg-neutral-50"
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);
